/**
 * First-run route resolution (guest-first onboarding).
 *
 * Pure decision for where a user lands when the app opens (native cold start).
 * The Welcome screen is the front door for anyone without a session; the tutorial
 * now sits *behind* its "Get Started" button, so `hasSeenTutorial` no longer gates
 * the entry route (see specs/guest-first-onboarding/spec.md).
 *
 * - Returning real (non-anonymous) user → straight into the app.
 * - Existing guest (anonymous session) → resume in the app.
 * - No session → the Welcome screen (Get Started / I already have an account).
 */

export type FirstRunRoute = 'app' | 'welcome';

export interface FirstRunAuthState {
  isSignedIn: boolean;
  isAnonymous: boolean;
}

export function resolveFirstRunRoute({ isSignedIn }: FirstRunAuthState): FirstRunRoute {
  // Any live session (real or guest) resumes in the app; a guest keeps their progress.
  if (isSignedIn) return 'app';
  // No session at all — show the Welcome front door.
  return 'welcome';
}

/**
 * True only for a returning, fully-registered user — the case where the native
 * cold-start effect should self-heal the local `has_seen_tutorial` flag.
 */
export function isReturningRealUser({ isSignedIn, isAnonymous }: FirstRunAuthState): boolean {
  return isSignedIn && !isAnonymous;
}
